import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Scale, AlertTriangle, CreditCard, Ban, Shield, Gavel, RefreshCw, Mail } from 'lucide-react';
import { motion } from 'framer-motion';

const TermsOfService = () => {
  const lastUpdated = "December 15, 2024";
  const effectiveDate = "December 15, 2024";

  return (
    <div className="min-h-screen bg-[#0F0F1A]" data-testid="terms-page">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-glass border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
            data-testid="terms-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white font-['Outfit']">Terms of Service</h1>
              <p className="text-slate-400 text-sm">2A Valuence Mobile Application</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4 text-sm text-slate-400 mb-8 pb-6 border-b border-white/10">
            <span>Last Updated: {lastUpdated}</span>
            <span>Effective Date: {effectiveDate}</span>
          </div>

          {/* Important Notice */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 sm:p-6 rounded-xl mb-8">
            <h3 className="text-yellow-400 font-bold mb-2 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              IMPORTANT - PLEASE READ CAREFULLY
            </h3>
            <p className="text-yellow-200/80 text-sm leading-relaxed">
              These Terms of Service ("Terms") constitute a legally binding agreement between you and Valuence Tech 
              ("Company," "we," "our," or "us") governing your use of the 2A Valuence mobile application. By 
              downloading, installing, or using the App, you agree to be bound by these Terms. If you do not agree, 
              do not use the App.
            </p>
          </div>

          <div className="prose prose-invert max-w-none space-y-8">

            {/* 1. Acceptance */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Scale className="w-5 h-5 text-primary" />
                1. Acceptance of Terms
              </h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p>
                  By accessing or using the 2A Valuence mobile application ("App"), you acknowledge that you have 
                  read, understood, and agree to be bound by these Terms of Service and our Privacy Policy. These 
                  Terms apply to all users of the App, including but not limited to businesses, resellers, repair 
                  shops, and individual users.
                </p>
                <p>
                  <strong>Eligibility:</strong> You must be at least 18 years old and have the legal capacity to 
                  enter into binding contracts to use this App. By using the App, you represent and warrant that 
                  you meet these requirements.
                </p>
                <p>
                  <strong>Business Use:</strong> If you are using the App on behalf of a business or organization, 
                  you represent that you have authority to bind that entity to these Terms, and the term "you" 
                  shall refer to both you individually and the entity.
                </p>
              </div>
            </section>

            {/* 2. Description of Service */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4">2. Description of Service</h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p>2A Valuence provides the following services through the App:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Device Pricing Intelligence:</strong> Market value estimates based on real-time data analysis</li>
                  <li><strong>Repair Management:</strong> Ticket creation, tracking, and workflow management</li>
                  <li><strong>Customer Relationship Management:</strong> Contact and transaction tracking</li>
                  <li><strong>Digital Waivers:</strong> Electronic signature capture and document storage</li>
                  <li><strong>Email Notifications:</strong> Automated repair status communications</li>
                  <li><strong>Parts Inventory:</strong> Stock tracking and management</li>
                </ul>
                <p className="mt-4">
                  We reserve the right to modify, suspend, or discontinue any part of the Service at any time 
                  without prior notice.
                </p>
              </div>
            </section>

            {/* 3. Pricing Data Disclaimer */}
            <section className="glass-card p-5 sm:p-6 border-2 border-yellow-500/30">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                3. PRICING DATA DISCLAIMER - IMPORTANT
              </h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <div className="bg-yellow-500/10 p-4 rounded-lg">
                  <p className="font-bold text-yellow-200 mb-2">THE PRICING INFORMATION PROVIDED BY 2A VALUENCE IS FOR INFORMATIONAL AND REFERENCE PURPOSES ONLY.</p>
                </div>
                
                <p><strong>3.1 No Guarantee of Accuracy:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Price estimates are based on algorithmic analysis of publicly available market data.</li>
                  <li>We do not guarantee the accuracy, completeness, or timeliness of any pricing information.</li>
                  <li>Market conditions change rapidly; prices may vary significantly from estimates.</li>
                  <li>Actual sale prices depend on factors beyond our control including condition assessment, buyer demand, and market timing.</li>
                </ul>

                <p className="mt-4"><strong>3.2 Your Responsibility:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>You are solely responsible for all pricing decisions you make.</li>
                  <li>You should independently verify pricing before making business decisions.</li>
                  <li>You accept all risk associated with relying on our pricing estimates.</li>
                </ul>

                <p className="mt-4"><strong>3.3 No Liability for Pricing:</strong></p>
                <p>
                  WE EXPRESSLY DISCLAIM ANY LIABILITY FOR LOSSES, DAMAGES, OR CLAIMS ARISING FROM YOUR USE OF 
                  OR RELIANCE ON OUR PRICING DATA. THIS INCLUDES BUT IS NOT LIMITED TO: lost profits, overpayment 
                  on trade-ins, inability to sell at estimated prices, or any other financial losses.
                </p>
              </div>
            </section>

            {/* 4. Account Registration */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4">4. Account Registration and Security</h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p><strong>4.1 Account Creation:</strong> To access certain features, you must create an account. You agree to:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Provide accurate, current, and complete information</li>
                  <li>Maintain and promptly update your account information</li>
                  <li>Keep your password confidential and secure</li>
                  <li>Notify us immediately of any unauthorized access</li>
                  <li>Accept responsibility for all activities under your account</li>
                </ul>

                <p className="mt-4"><strong>4.2 Account Termination:</strong> We reserve the right to suspend or terminate your account at any time for:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Violation of these Terms</li>
                  <li>Fraudulent, abusive, or illegal activity</li>
                  <li>Non-payment of fees (if applicable)</li>
                  <li>Extended periods of inactivity</li>
                  <li>Any other reason at our sole discretion</li>
                </ul>
              </div>
            </section>

            {/* 5. User Conduct */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Ban className="w-5 h-5 text-primary" />
                5. Prohibited Conduct
              </h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p>You agree NOT to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Use the App for any illegal purpose or in violation of any laws</li>
                  <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                  <li>Interfere with or disrupt the App's functionality or servers</li>
                  <li>Scrape, harvest, or collect data from the App without permission</li>
                  <li>Reverse engineer, decompile, or disassemble the App</li>
                  <li>Use automated systems, bots, or scripts to access the App</li>
                  <li>Upload malicious code, viruses, or harmful content</li>
                  <li>Impersonate any person or entity</li>
                  <li>Use the App to harass, abuse, or harm others</li>
                  <li>Circumvent any security features or access restrictions</li>
                  <li>Resell, redistribute, or commercialize our pricing data</li>
                  <li>Use the App in any way that could damage our reputation or business</li>
                </ul>
              </div>
            </section>

            {/* 6. Intellectual Property */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4">6. Intellectual Property Rights</h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p><strong>6.1 Our Property:</strong></p>
                <p>
                  The App and its entire contents, features, and functionality (including but not limited to: 
                  software, code, algorithms, user interface, graphics, logos, and trademarks) are owned by 
                  Valuence Tech and are protected by copyright, trademark, patent, trade secret, and other 
                  intellectual property laws.
                </p>

                <p className="mt-4"><strong>6.2 Limited License:</strong></p>
                <p>
                  We grant you a limited, non-exclusive, non-transferable, revocable license to use the App 
                  for your personal or internal business purposes, subject to these Terms. This license does 
                  not include the right to:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Modify, copy, or create derivative works</li>
                  <li>Sublicense, sell, or transfer the App</li>
                  <li>Use the App for competing purposes</li>
                  <li>Remove any proprietary notices or labels</li>
                </ul>

                <p className="mt-4"><strong>6.3 Your Content:</strong></p>
                <p>
                  You retain ownership of content you submit to the App (customer data, repair notes, etc.). 
                  By submitting content, you grant us a worldwide, royalty-free license to use, store, and 
                  process that content solely to provide the Service.
                </p>
              </div>
            </section>

            {/* 7. Fees and Payment */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                7. Fees and Payment
              </h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p><strong>7.1 Free and Paid Features:</strong></p>
                <p>
                  The App may offer both free and premium (paid) features. Access to certain features may 
                  require a subscription or one-time payment.
                </p>

                <p className="mt-4"><strong>7.2 Subscription Terms:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Subscriptions automatically renew unless cancelled before the renewal date</li>
                  <li>You authorize us to charge your payment method for recurring fees</li>
                  <li>Prices may change with 30 days' notice to existing subscribers</li>
                  <li>All fees are non-refundable except as required by law</li>
                </ul>

                <p className="mt-4"><strong>7.3 Cancellation:</strong></p>
                <p>
                  You may cancel your subscription at any time through the App or by contacting us. 
                  Cancellation takes effect at the end of the current billing period.
                </p>

                <p className="mt-4"><strong>7.4 Refund Policy:</strong></p>
                <p>
                  Except as required by applicable law, all payments are final and non-refundable. 
                  For iOS purchases, refunds are subject to Apple's refund policies.
                </p>
              </div>
            </section>

            {/* 8. Disclaimer of Warranties */}
            <section className="glass-card p-5 sm:p-6 border-2 border-red-500/30">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                8. DISCLAIMER OF WARRANTIES
              </h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <div className="bg-red-500/10 p-4 rounded-lg text-sm">
                  <p className="uppercase font-bold text-red-300">
                    THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
                    EITHER EXPRESS OR IMPLIED.
                  </p>
                </div>
                
                <p>TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT</li>
                  <li>WARRANTIES THAT THE APP WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE</li>
                  <li>WARRANTIES REGARDING THE ACCURACY OR RELIABILITY OF ANY INFORMATION OBTAINED THROUGH THE APP</li>
                  <li>WARRANTIES THAT DEFECTS WILL BE CORRECTED</li>
                  <li>WARRANTIES THAT THE APP IS FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS</li>
                </ul>

                <p className="mt-4">
                  YOU USE THE APP AT YOUR OWN RISK. WE DO NOT WARRANT THAT THE APP WILL MEET YOUR REQUIREMENTS 
                  OR ACHIEVE ANY INTENDED RESULTS.
                </p>
              </div>
            </section>

            {/* 9. Limitation of Liability */}
            <section className="glass-card p-5 sm:p-6 border-2 border-red-500/30">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-500" />
                9. LIMITATION OF LIABILITY
              </h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <div className="bg-red-500/10 p-4 rounded-lg text-sm">
                  <p className="uppercase font-bold text-red-300">
                    TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:
                  </p>
                </div>

                <p><strong>9.1 No Consequential Damages:</strong></p>
                <p>
                  IN NO EVENT SHALL VALUENCE TECH, ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, 
                  OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE 
                  DAMAGES, INCLUDING WITHOUT LIMITATION:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Loss of profits, revenue, or business</li>
                  <li>Loss of data or information</li>
                  <li>Loss of goodwill or reputation</li>
                  <li>Cost of substitute services</li>
                  <li>Any other intangible losses</li>
                </ul>

                <p className="mt-4"><strong>9.2 Liability Cap:</strong></p>
                <p>
                  OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM OR RELATED TO THESE TERMS OR YOUR USE OF 
                  THE APP SHALL NOT EXCEED THE GREATER OF: (A) THE AMOUNT YOU PAID US IN THE TWELVE (12) 
                  MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED DOLLARS ($100).
                </p>

                <p className="mt-4"><strong>9.3 Essential Basis:</strong></p>
                <p>
                  THESE LIMITATIONS APPLY REGARDLESS OF THE LEGAL THEORY (CONTRACT, TORT, STRICT LIABILITY, 
                  OR OTHERWISE), EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                </p>

                <p className="mt-4 text-sm text-slate-400">
                  Some jurisdictions do not allow the exclusion of certain warranties or limitation of liability, 
                  so some of the above limitations may not apply to you.
                </p>
              </div>
            </section>

            {/* 10. Indemnification */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4">10. Indemnification</h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p>
                  You agree to defend, indemnify, and hold harmless Valuence Tech and its officers, directors, 
                  employees, agents, licensors, and suppliers from and against any claims, actions, demands, 
                  liabilities, damages, losses, costs, and expenses (including reasonable attorneys' fees) 
                  arising from or related to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Your use or misuse of the App</li>
                  <li>Your violation of these Terms</li>
                  <li>Your violation of any third-party rights</li>
                  <li>Your violation of any applicable laws or regulations</li>
                  <li>Any content you submit or transmit through the App</li>
                  <li>Any business decisions you make based on our pricing data</li>
                </ul>
              </div>
            </section>

            {/* 11. Dispute Resolution */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Gavel className="w-5 h-5 text-primary" />
                11. Dispute Resolution
              </h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p><strong>11.1 Informal Resolution:</strong></p>
                <p>
                  Before initiating any formal dispute resolution, you agree to contact us at 2avaluence@gmail.com 
                  and attempt to resolve the dispute informally for at least 30 days.
                </p>

                <p className="mt-4"><strong>11.2 Binding Arbitration:</strong></p>
                <p>
                  If informal resolution fails, any dispute arising from these Terms shall be resolved by 
                  binding arbitration administered by the American Arbitration Association under its Consumer 
                  Arbitration Rules. The arbitration shall be conducted in English, and the arbitrator's 
                  decision shall be final and binding.
                </p>

                <p className="mt-4"><strong>11.3 Class Action Waiver:</strong></p>
                <p className="bg-yellow-500/10 p-3 rounded-lg">
                  YOU AGREE TO RESOLVE ANY DISPUTES ON AN INDIVIDUAL BASIS AND WAIVE ANY RIGHT TO PARTICIPATE 
                  IN A CLASS ACTION LAWSUIT OR CLASS-WIDE ARBITRATION.
                </p>

                <p className="mt-4"><strong>11.4 Exceptions:</strong></p>
                <p>
                  Either party may seek injunctive relief in court for intellectual property infringement 
                  or unauthorized access to the App.
                </p>
              </div>
            </section>

            {/* 12. Governing Law */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4">12. Governing Law</h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p>
                  These Terms and your use of the App shall be governed by and construed in accordance with 
                  the laws of the United States and the State of Delaware, without regard to conflict of 
                  law principles.
                </p>
                <p>
                  Any legal action not subject to arbitration shall be brought exclusively in the state or 
                  federal courts located in Delaware, and you consent to the jurisdiction of such courts.
                </p>
              </div>
            </section>

            {/* 13. Termination */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4">13. Termination</h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p><strong>13.1 Your Right to Terminate:</strong></p>
                <p>You may terminate your account at any time by contacting us or using the account deletion feature in the App.</p>

                <p className="mt-4"><strong>13.2 Our Right to Terminate:</strong></p>
                <p>
                  We may suspend or terminate your access immediately, without prior notice or liability, 
                  for any reason, including breach of these Terms.
                </p>

                <p className="mt-4"><strong>13.3 Effect of Termination:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Your license to use the App terminates immediately</li>
                  <li>You must cease all use of the App</li>
                  <li>Your data may be deleted in accordance with our Privacy Policy</li>
                  <li>Sections that by their nature should survive will continue to apply</li>
                </ul>
              </div>
            </section>

            {/* 14. Changes to Terms */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-primary" />
                14. Changes to These Terms
              </h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p>
                  We reserve the right to modify these Terms at any time. When we make material changes:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>We will update the "Last Updated" date</li>
                  <li>We will notify you via email or in-app notification</li>
                  <li>Changes take effect 30 days after notification for existing users</li>
                </ul>
                <p className="mt-4">
                  Your continued use of the App after changes become effective constitutes acceptance of 
                  the modified Terms. If you do not agree, you must stop using the App.
                </p>
              </div>
            </section>

            {/* 15. General Provisions */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4">15. General Provisions</h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p><strong>15.1 Entire Agreement:</strong> These Terms, together with the Privacy Policy, constitute the entire agreement between you and us.</p>
                
                <p><strong>15.2 Severability:</strong> If any provision is found unenforceable, the remaining provisions remain in effect.</p>
                
                <p><strong>15.3 Waiver:</strong> Our failure to enforce any right does not constitute a waiver of that right.</p>
                
                <p><strong>15.4 Assignment:</strong> You may not assign these Terms without our consent. We may assign our rights freely.</p>
                
                <p><strong>15.5 Force Majeure:</strong> We are not liable for delays or failures due to circumstances beyond our reasonable control.</p>
                
                <p><strong>15.6 No Third-Party Beneficiaries:</strong> These Terms do not create rights for any third party.</p>
              </div>
            </section>

            {/* 16. Contact */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                16. Contact Information
              </h2>
              <div className="text-slate-300 leading-relaxed">
                <p>For questions about these Terms of Service, please contact us:</p>
                <div className="bg-white/5 p-4 rounded-lg mt-4 space-y-2">
                  <p><strong className="text-white">2A Valuence / Valuence Tech</strong></p>
                  <p><strong>Email:</strong> 2avaluence@gmail.com</p>
                  <p><strong>Website:</strong> https://2avaluence.com</p>
                  <p><strong>Support:</strong> https://2avaluence.com/support</p>
                </div>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="glass-card p-5 sm:p-6 border-2 border-primary/30">
              <h2 className="text-xl font-bold text-white mb-4">17. Acknowledgment</h2>
              <div className="text-slate-300 leading-relaxed">
                <p>
                  BY USING THE 2A VALUENCE APP, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS OF SERVICE, 
                  UNDERSTAND THEM, AND AGREE TO BE BOUND BY THEM. IF YOU DO NOT AGREE TO THESE TERMS, 
                  YOU ARE NOT AUTHORIZED TO USE THE APP.
                </p>
              </div>
            </section>

          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-white/10 text-center">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} 2A Valuence. All rights reserved.
            </p>
            <div className="flex justify-center gap-6 mt-4">
              <Link to="/privacy" className="text-primary hover:underline text-sm">Privacy Policy</Link>
              <Link to="/support" className="text-primary hover:underline text-sm">Support</Link>
              <Link to="/" className="text-primary hover:underline text-sm">Home</Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfService;
